import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import SectionReveal from "@/components/SectionReveal";
import { trackCta, trackPathway } from "@/lib/analytics";
import { ADDRESS } from "@/components/LandingBits";

const TRIAL = "/30-day-trial";
const COACHING = "/group-personal-training-windermere";
const GYM = "/gym-windermere";

const BENEFITS = [
  { t: "Know what to do.", d: "Every session is planned and coached. You arrive, we tell you what's on and show you how — no guessing, no wandering." },
  { t: "Train at your level.", d: "Small groups mean your coach can see you, adjust the exercise and progress it when you're ready. Beginners and returners are the norm." },
  { t: "Build a routine you can keep.", d: "Fixed session times, people who expect to see you and a plan that fits real life. Consistency is the whole point." },
];

const LIFE = [
  ["/gallery/kore-training-2.webp", "Coached strength work on the KORE gym floor", "md:col-span-7 aspect-[4/3]"],
  ["/gallery/community-women.webp", "KORE members training together", "md:col-span-5 aspect-[4/3] md:aspect-[3/4]"],
  ["/gallery/community-social.webp", "KORE members at a club social", "md:col-span-5 aspect-[4/3] md:aspect-[3/4]"],
  ["/gallery/community-kombat.webp", "Members after a KORE Kombat session", "md:col-span-7 aspect-[4/3]"],
];

const STEPS = [
  { t: "Arrange a visit.", d: "Send us your details and we'll be in touch within 24 hours to arrange a look around and a chat about what you're after." },
  { t: "Start your 30-day trial.", d: "£99, one-off. Coached sessions for a month so you can see how KORE fits you — no tie-in." },
  { t: "Discuss what comes next.", d: "At the end of the 30 days we sit down together. Continue on KORE Coaching at £149/month, switch to Access, or simply say thanks." },
];

const FAQS = [
  { q: "I'm a complete beginner — is that OK?", a: "Yes, and common. Most people who start at KORE haven't trained in years. Every exercise is coached and scaled to you." },
  { q: "When are sessions?", a: "Coaching runs twice a week: your group trains on either Monday & Wednesday or Tuesday & Thursday, at 6am, 7am or 8am. Pick your preferred group on the trial form. Open gym hours: Mon & Wed 09:00–18:00, Tue & Fri 09:00–14:00, Thu 11:00–18:00, Sat 08:30–14:00, Sun 08:30–12:00." },
  { q: "Coaching or Access — which is right for me?", a: "If you want a plan, a coach and accountability, Coaching. If you're confident training on your own and just want a calm, well-equipped gym, Access. Not sure? Come for a look around and we'll tell you straight." },
  { q: "Is there parking?", a: "Yes — parking at Rowan House on Beresford Road, a couple of minutes from the lake and Bowness town centre." },
  { q: "Where are you?", a: `${ADDRESS.name}, ${ADDRESS.street}, ${ADDRESS.locality}, ${ADDRESS.region} ${ADDRESS.postcode}.` },
  { q: "What happens after the trial?", a: "Nothing automatic. We sit down together and you decide: continue on KORE Coaching at £149/month, move to KORE Access, or stop. No pressure either way." },
];

export default function Home() {
  const [tests, setTests] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setTests(r.data)).catch(() => {}); }, []);
  const proof = tests[0];

  const trialClick = (loc) => () => trackCta({ cta_name: "home_trial", cta_text: "Explore the £99 Trial", destination_url: TRIAL, cta_location: loc });

  return (
    <div data-testid="home-page">
      <Seo
        title="The Gym for People 40+ in Bowness-on-Windermere"
        description="KORE Wellbeing is the coaching club for people 40+ in Bowness-on-Windermere: small-group coaching, a calm independent gym and a community you'll enjoy training alongside. £99 30-day trial."
        path="/"
        jsonLd={{
          "@context": "https://schema.org", "@type": "HealthClub", name: "Kore Wellbeing", url: "https://korewellbeing.com",
          telephone: "+447429565591", email: "info@korewellbeing.com", image: "https://korewellbeing.com/gallery/kore-training-1.webp",
          address: { "@type": "PostalAddress", streetAddress: ADDRESS.street, addressLocality: ADDRESS.locality, addressRegion: ADDRESS.region, postalCode: ADDRESS.postcode, addressCountry: "GB" },
        }}
      />

      {/* A · HERO */}
      <section data-testid="hero" className="bg-kore-warm pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="container-kore grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">Bowness-on-Windermere</p>
            <h1 data-testid="hero-headline" className="display-1 text-kore-forest mt-5">Get stronger.<br />Feel better.<br />Enjoy the process.</h1>
            <p className="mt-7 text-lg md:text-xl text-kore-ink/75 leading-relaxed max-w-xl">Small-group coaching for people 40+, with a clear plan, personal support and people you'll enjoy training alongside.</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to={TRIAL} data-testid="hero-cta-primary" onClick={trialClick("hero")} className="btn-primary">Explore the £99 Trial <ArrowRight size={16} /></Link>
              <Link to={`${GYM}#look-around`} data-testid="hero-cta-secondary" onClick={() => trackCta({ cta_name: "home_visit", cta_text: "Come and have a look around", destination_url: `${GYM}#look-around`, cta_location: "hero" })} className="btn-ghost">Come and have a look around</Link>
            </div>
            <p className="mt-6 text-[15px] text-kore-ink/60">Complete beginners welcome. Coaching adapted to you.</p>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden bg-kore-stone aspect-[4/3] lg:aspect-[5/4]">
              <img src="/gallery/community-mixed.webp" alt="KORE members together on the gym floor after a coached small-group session in Bowness-on-Windermere" className="w-full h-full object-cover object-[50%_35%]" fetchPriority="high" width="1568" height="1176" />
            </div>
          </div>
        </div>
      </section>

      {/* B · MEMBER PROOF */}
      {proof && (
        <section data-testid="home-proof" className="bg-kore-warm py-16 md:py-20 border-b border-kore-stone">
          <div className="container-kore max-w-3xl">
            <div className="flex gap-1" aria-label={`${proof.rating} star review`}>{Array.from({ length: proof.rating || 5 }).map((_, i) => <Star key={i} size={14} className="text-kore-forest fill-kore-forest" />)}</div>
            <blockquote className="mt-5 font-quote text-2xl md:text-[2rem] leading-snug text-kore-ink">"{proof.quote}"</blockquote>
            <p className="mt-5 text-[15px] text-kore-ink/65"><span className="font-semibold text-kore-ink">{proof.name}</span> · {proof.role}{proof.source_url && <> · <a href={proof.source_url} target="_blank" rel="noreferrer" className="underline">Read on Google</a></>}</p>
          </div>
        </section>
      )}

      {/* C · COACHING IN ACTION */}
      <SectionReveal as="section" data-testid="home-coaching" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">Coaching in action</p>
            <h2 className="display-2 text-kore-forest mt-4">A proper plan. A coach beside you.</h2>
            <ul className="mt-10 space-y-8">
              {BENEFITS.map((b) => (
                <li key={b.t}>
                  <h3 className="text-xl md:text-2xl font-semibold text-kore-forest">{b.t}</h3>
                  <p className="mt-2 text-kore-ink/75 leading-relaxed">{b.d}</p>
                </li>
              ))}
            </ul>
            <Link to={COACHING} data-testid="home-coaching-link" onClick={() => trackCta({ cta_name: "home_coaching_more", cta_text: "More about Coaching", destination_url: COACHING, cta_location: "body" })} className="btn-link mt-10">More about KORE Coaching <ArrowRight size={14} /></Link>
          </div>
          <figure className="lg:col-span-6">
            <div className="rounded-2xl overflow-hidden bg-kore-stone aspect-[9/16] max-w-sm mx-auto lg:max-w-none lg:aspect-[4/5]">
              <video data-testid="home-typical-week-video" src="/gallery/typical-week.mp4" poster="/gallery/typical-week-poster.webp" controls muted playsInline preload="metadata" className="w-full h-full object-cover" aria-label="A typical coached week at KORE Wellbeing" />
            </div>
            <figcaption className="mt-3 text-[13px] text-kore-ink/55">Filmed on the KORE training floor, Bowness-on-Windermere. Press play — no sound needed.</figcaption>
          </figure>
        </div>
      </SectionReveal>

      {/* D · TWO WAYS TO TRAIN */}
      <SectionReveal as="section" data-testid="home-pathways" className="bg-kore-stone py-24 md:py-32">
        <div className="container-kore">
          <div className="max-w-2xl">
            <p className="eyebrow">Two ways to train</p>
            <h2 className="display-2 text-kore-forest mt-4">Coached, or on your own terms.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-12 gap-6">
            <article data-testid="pathway-card-coached" className="md:col-span-7 bg-kore-forest text-white rounded-2xl p-8 md:p-12 flex flex-col">
              <p className="eyebrow text-kore-light">KORE Coaching · Flagship</p>
              <h3 className="mt-4 text-3xl md:text-4xl font-semibold">For people who want structure, guidance and accountability.</h3>
              <ul className="mt-7 space-y-3 text-white/85">
                {["Two coached Strength sessions a week — Mon & Wed or Tue & Thu", "A structured plan that progresses", "Exercises adapted to you on the day", "A community of people 40+"].map((x) => <li key={x} className="flex gap-3"><Check size={18} className="text-kore-light shrink-0 mt-1" />{x}</li>)}
              </ul>
              <div className="mt-9 pt-7 border-t border-white/15">
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                  <div><span className="text-4xl font-semibold">£99</span> <span className="text-white/70">for the 30-day trial</span></div>
                  <div className="text-white/70">then <span className="text-white font-semibold">£149/month</span> if you choose to continue</div>
                </div>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to={TRIAL} data-testid="pathway-cta-coached" onClick={() => { trackPathway({ pathway: "coached", cta_text: "Explore the £99 Trial", destination_url: TRIAL }); trialClick("pathways")(); }} className="btn-gold">Explore the £99 Trial <ArrowRight size={16} /></Link>
                <Link to={COACHING} className="inline-flex items-center gap-2 text-[15px] font-semibold text-white/90 hover:text-white min-h-[44px]">About Coaching <ArrowRight size={14} /></Link>
              </div>
            </article>
            <article data-testid="pathway-card-access" className="md:col-span-5 bg-white border border-kore-stone rounded-2xl p-8 md:p-10 flex flex-col">
              <p className="eyebrow">KORE Access · Gym membership</p>
              <h3 className="mt-4 text-2xl md:text-3xl font-semibold text-kore-forest">For people who prefer to train independently.</h3>
              <ul className="mt-6 space-y-3 text-kore-ink/75">
                {["Quality strength and cardio equipment", "A calm, uncrowded floor", "Open gym weekdays from 09:00 · weekends from 08:30"].map((x) => <li key={x} className="flex gap-3"><Check size={18} className="text-kore-forest shrink-0 mt-1" />{x}</li>)}
              </ul>
              <div className="mt-auto pt-7 border-t border-kore-stone">
                <div className="text-kore-ink"><span className="text-3xl font-semibold">£39</span><span className="text-kore-ink/65">/month on a 12-month plan</span></div>
                <div className="mt-1 text-kore-ink/75"><span className="font-semibold text-kore-ink">£49</span>/month rolling</div>
              </div>
              <Link to={GYM} data-testid="pathway-cta-access" onClick={() => trackPathway({ pathway: "access", cta_text: "Explore the Gym", destination_url: GYM })} className="btn-ghost mt-7 self-start">Explore the Gym <ArrowRight size={16} /></Link>
            </article>
          </div>
          <p className="mt-6 text-[15px] text-kore-ink/60">Not sure which suits you? <Link to="/help-me-choose" data-testid="pathway-help" className="underline text-kore-forest">Answer four quick questions</Link> or just come and have a look around.</p>
        </div>
      </SectionReveal>

      {/* E · LIFE AT KORE */}
      <SectionReveal as="section" id="life-at-kore" data-testid="home-life" className="bg-kore-warm py-24 md:py-32 scroll-mt-20">
        <div className="container-kore">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-none">
            <div className="max-w-2xl">
              <p className="eyebrow">Life at KORE</p>
              <h2 className="display-2 text-kore-forest mt-4">Real training. Real conversations. A club you'll want to be part of.</h2>
            </div>
            <p className="text-kore-ink/65 md:max-w-xs">Classes, a women-only foundations group, a Thursday nutrition group and the odd social. All under one roof at Rowan House.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-12 gap-4 md:gap-6">
            {LIFE.map(([src, alt, cls]) => (
              <div key={src} className={`${cls} rounded-2xl overflow-hidden bg-kore-stone`}><img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" /></div>
            ))}
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {[
              ["/classes", "Classes", "Ascent, Zones, Blast and Kombat — coached group sessions.", "home-life-classes"],
              ["/womens-strength-foundations", "Women's Strength Foundations", "A women-only six-week beginners' group. Join the waiting list.", "home-life-womens"],
              ["/fuel-chapters", "Fuel Chapters", "Practical nutrition support with Georgie.", "home-life-fuel"],
            ].map(([to, t, d, tid]) => (
              <Link key={to} to={to} data-testid={tid} className="group bg-white border border-kore-stone rounded-2xl p-6 hover:border-kore-forest/40 transition-colors">
                <h3 className="text-lg font-semibold text-kore-forest">{t}</h3>
                <p className="mt-2 text-[15px] text-kore-ink/70">{d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-[14px] font-semibold text-kore-forest">{t === "Fuel Chapters" ? "Explore Fuel Chapters" : "Find out more"} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </SectionReveal>

      {/* F · MEET THE COACH */}
      <SectionReveal as="section" data-testid="home-coach" className="bg-kore-warm pb-24 md:pb-32">
        <div className="container-kore grid md:grid-cols-12 gap-10 items-center border-t border-kore-stone pt-20">
          <div className="md:col-span-4">
            <div className="rounded-2xl overflow-hidden aspect-[3/4] bg-kore-stone"><img src="/gallery/founder-dale.webp" alt="Dale, founder and coach at KORE Wellbeing" loading="lazy" className="w-full h-full object-cover" /></div>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="eyebrow">Meet the coach</p>
            <h2 className="display-2 text-kore-forest mt-4">Dale, founder of KORE.</h2>
            <p className="mt-6 text-kore-ink/80 leading-relaxed">Dale opened KORE because he was tired of seeing good people quit unfriendly gyms. He works with a friendly community of 40+ members, and the reason so many of them stay for years is simple — they actually enjoy coming.</p>
            <p className="mt-4 text-kore-ink/80 leading-relaxed">Good health isn't built through quick fixes or hours in the gym. It's built through consistency, support and habits that fit into real life. That's what KORE is for.</p>
            <Link to="/about" data-testid="home-coach-about" className="btn-link mt-8">More about KORE <ArrowRight size={14} /></Link>
          </div>
        </div>
      </SectionReveal>

      {/* G · GETTING STARTED */}
      <SectionReveal as="section" data-testid="home-steps" className="bg-kore-forest text-white py-24 md:py-32">
        <div className="container-kore">
          <div className="max-w-2xl">
            <p className="eyebrow text-kore-light">Getting started</p>
            <h2 className="display-2 mt-4">Three steps. No pressure.</h2>
          </div>
          <ol className="mt-12 grid md:grid-cols-3 gap-10">
            {STEPS.map((s, i) => (
              <li key={s.t}>
                <div className="text-kore-light font-semibold">0{i + 1}</div>
                <h3 className="mt-3 text-2xl font-semibold">{s.t}</h3>
                <p className="mt-3 text-white/75 leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </SectionReveal>

      {/* H · QUESTIONS + LOCATION */}
      <SectionReveal as="section" data-testid="home-faq" className="bg-kore-warm py-24 md:py-32">
        <div className="container-kore grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Practical questions</p>
            <h2 className="display-2 text-kore-forest mt-4">The things people ask first.</h2>
            <address className="not-italic mt-8 text-kore-ink/75 leading-relaxed">
              <strong className="text-kore-ink">{ADDRESS.name}</strong><br />{ADDRESS.street}<br />{ADDRESS.locality}<br />{ADDRESS.region} {ADDRESS.postcode}
            </address>
            <a href="https://www.google.com/maps/search/?api=1&query=Rowan+House+Beresford+Road+Bowness-on-Windermere+LA23+2JG" target="_blank" rel="noreferrer" data-testid="home-map-link" className="btn-link mt-4">Open in Maps <ArrowRight size={14} /></a>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Accordion type="single" collapsible>
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`f-${i}`} data-testid={`home-faq-${i}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold text-kore-forest py-5 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-kore-ink/75 leading-relaxed text-base pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </SectionReveal>

      {/* FINAL CTA */}
      <section data-testid="home-final-cta" className="bg-kore-stone py-24 md:py-28">
        <div className="container-kore max-w-3xl text-center">
          <h2 className="display-2 text-kore-forest">Thirty days, coached. Then you decide.</h2>
          <p className="mt-5 text-kore-ink/70">£99, one-off, no tie-in. Complete beginners welcome.</p>
          <Link to={TRIAL} data-testid="home-final-trial" onClick={trialClick("footer")} className="btn-primary mt-9">Explore the £99 Trial <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
